import { inject, injectable } from "inversify";
import { Md5 } from "ts-md5/dist/md5";
import { VintomError } from "../../api/model/error/VintomError";
import { AnalyticsEndpoint } from "../../model/playerConfiguration/AnalyticsEndpoint";
import { CookieHelper } from "../../utils/CookieHelper";
import { TYPES } from "../di/types";
import {
  AnalyticsEvent,
  CTAActionEvent,
  CTAShowEvent,
  NotSupportedBrowserEvent,
  PlayerErrorEvent,
  PlayerInitEvent,
  PlayerStartEvent,
  ScenarioElementEndReason,
  ScenarioElementPlayingEvent,
  ScenarioElementProgress,
  ScenarioElementProgressEvent,
  ScenarioElementStartEvent
} from "./AnalyticEvent";
import { AnalyticsSender } from "./AnalyticsSender";
import { GoogleAnalyticsSender, GoogleAnalyticsSenderImpl } from "./GoogleAnalyticsSender";
import { VintomAnalyticsSender } from "./VintomAnalyticsSender";

export interface AnalyticsCollector {
  initialize(videoCode: string, videoDate: number, endpoints: AnalyticsEndpoint[], campaign: string, groupId: number): void;
  logPlayerInit(scenarioElementId: number): void;
  logPlayerStart(scenarioElementId: number): void;
  logPlayerError(scenarioElementId: number, error: VintomError): void;
  logScenarioElementStart(scenarioElementId: number, videoType: string, volume: number, duration: number): void;
  logScenarioElementPlaying(
    scenarioElementId: number,
    videoType: string,
    volume: number,
    duration: number,
    currentTime: number,
    endReason?: ScenarioElementEndReason): void;
  logScenarioElementProgress(scenarioElementId: number, progress: ScenarioElementProgress): void;
  logCTAShow(scenarioElementId: number, ctaId: number): void;
  logCTAAction(scenarioElementId: number, ctaId: number, actionId: number, actionAdditionalData: object): void;
  logNotSupportedBrowser(): void;
}

@injectable()
export class AnalyticsCollectorImpl implements AnalyticsCollector {

  private static USER_IDENTIFIER_COOKIE_NAME = "vintomUserIdentifier";

  private projectCode: string;
  private groupId: number;
  private videoCode: string;
  private videoDate: number;
  private userIdentifier: string;
  private lastPlayEventTime: number;
  private configuredSenders: AnalyticsSender[] = [];
  private googleAnalyticsEndpointExists: boolean = false;
  private googleAnalyticsEventQueue: AnalyticsEvent[] = [];

  constructor(
    @inject(TYPES.analyticsCollector.GoogleAnalyticsSender) private googleAnalyticsSender: GoogleAnalyticsSender,
    @inject(TYPES.analyticsCollector.VintomAnalyticsSender) private vintomAnalyticsSender: VintomAnalyticsSender) {}

  initialize(videoCode: string, videoDate: number, endpoints: AnalyticsEndpoint[], campaign: string, groupId: number) {
    this.projectCode = campaign;
    this.groupId = groupId;
    this.videoCode = videoCode;
    this.videoDate = videoDate;
    this.userIdentifier = this.readUserIdentifierFromCookie();
    if (!this.userIdentifier) {
      this.userIdentifier = this.createUserIdentifier();
      this.saveUserIdentifierToCookie(this.userIdentifier);
    }

    const gaTrackers = endpoints.filter((endpoint) => endpoint.provider === "GA").map((endpoint) => endpoint.gaServiceTracker);
    if (gaTrackers.length) {
      this.googleAnalyticsEndpointExists = true;
      this.googleAnalyticsSender.initialize(gaTrackers, campaign)
        .then(() => this.configuredSenders.push(this.googleAnalyticsSender))
        .then(() => this.processGoogleAnalyticsEventQueue());
    }

    const vintomEndpoints = endpoints.filter((endpoint) => endpoint.provider === "vintom").map((endpoint) => endpoint.vintomServer);
    if (vintomEndpoints.length) {
      this.vintomAnalyticsSender.initialize(vintomEndpoints);
      this.configuredSenders.push(this.vintomAnalyticsSender);
    }

  }

  logPlayerInit(scenarioElementId: number) {
    this.sendEvent(new PlayerInitEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate
    ));
  }

  logPlayerStart(scenarioElementId: number) {
    this.sendEvent(new PlayerStartEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate
    ));
  }

  logPlayerError(scenarioElementId: number, error: VintomError) {
    this.sendEvent(new PlayerErrorEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      error.errorCode,
      error.message
    ));
  }

  logScenarioElementStart(scenarioElementId: number, videoType: string, volume: number, duration: number) {
    this.sendEvent(new ScenarioElementStartEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      videoType,
      volume,
      duration
    ));

    this.lastPlayEventTime = 0;
  }

  logScenarioElementPlaying(
    scenarioElementId: number,
    videoType: string,
    volume: number,
    duration: number,
    currentTime: number,
    endReason?: ScenarioElementEndReason) {

    this.sendEvent(new ScenarioElementPlayingEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      videoType,
      volume,
      duration,
      currentTime,
      this.lastPlayEventTime,
      endReason
    ));

    this.lastPlayEventTime = currentTime;
  }

  logScenarioElementProgress(scenarioElementId: number, progress: ScenarioElementProgress) {
    this.sendEvent(new ScenarioElementProgressEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      progress
    ));
  }

  logCTAShow(scenarioElementId: number, ctaId: number) {
    this.sendEvent(new CTAShowEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      ctaId
    ));
  }

  logCTAAction(scenarioElementId: number, ctaId: number, actionId: number, actionAdditionalData: object) {
    this.sendEvent(new CTAActionEvent(
      this.projectCode,
      this.groupId,
      scenarioElementId,
      this.videoCode,
      this.userIdentifier,
      this.videoDate,
      ctaId,
      actionId,
      actionAdditionalData
    ));
  }

  logNotSupportedBrowser() {
    this.sendEvent(new NotSupportedBrowserEvent(
        this.projectCode,
        this.groupId,
        null,
        this.videoCode,
        this.userIdentifier,
        this.videoDate
    ));
  }

  private sendEvent(event: AnalyticsEvent) {
    for (const sender of this.configuredSenders) {
      sender.sendEvent(event);
    }
    if (!this.googleAnalyticsSenderInitialized()) {
      this.googleAnalyticsEventQueue.push(event);
    }
  }

  private googleAnalyticsSenderInitialized(): boolean {
    if (!this.googleAnalyticsEndpointExists) {
      return true;
    } else {
      for (const sender of this.configuredSenders) {
        if (sender instanceof GoogleAnalyticsSenderImpl) {
          return true;
        }
      }
    }
    return false;
  }

  private processGoogleAnalyticsEventQueue(): void {
    for (const event of this.googleAnalyticsEventQueue) {
      this.googleAnalyticsSender.sendEvent(event);
    }
    this.googleAnalyticsEventQueue = [];
  }

  private createUserIdentifier(): string {
    return Md5.hashStr(Math.random() + "") as string;
  }

  private saveUserIdentifierToCookie(identifier: string) {
    CookieHelper.setCookie(AnalyticsCollectorImpl.USER_IDENTIFIER_COOKIE_NAME, identifier);
  }

  private readUserIdentifierFromCookie() {
    return CookieHelper.readCookie(AnalyticsCollectorImpl.USER_IDENTIFIER_COOKIE_NAME);
  }

}
